import { cn } from "@design-lab/shared";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@design-lab/shared/ui/modal";
import { useEffect, useRef, useState } from "react";
import { ZOOM_CONFIG, calculateContainerDimensions } from "../../lib";
import { useZoomAndPan, type Dimensions } from "../../model";
import { ZoomControls } from "./ZoomControls";

type ArtworkPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageAlt: string;
  onSelect?: (() => void) | undefined;
  isSelected?: boolean;
};

export function ArtworkPreviewModal({
  isOpen,
  onClose,
  imageSrc,
  imageAlt,
  onSelect,
  isSelected = false
}: ArtworkPreviewModalProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<Dimensions>({ width: 0, height: 0 });
  const { isDesktop } = useResponsive();
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const {
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    zoomPercentage,
    panOffset,
    isPanning,
    resetPan,
    handlers: panHandlers
  } = useZoomAndPan({
    isDesktop,
    containerRef,
    imageLoaded
  });

  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
      setImageLoaded(true);
    }
  };

  const containerDimensions = calculateContainerDimensions(isDesktop, imageLoaded, imageDimensions);

  // Prevent page zoom with document-level listener
  useEffect(() => {
    if (!isOpen) return undefined;

    const preventPageZoom = (e: WheelEvent) => {
      // Check if the event is coming from within our modal container
      if (imageContainerRef?.current?.contains(e.target as Node)) {
        // Prevent page zoom but allow event to continue to our React handlers
        e.preventDefault();
      }
    };

    // Add document-level listener with passive: false and capture: true
    // This catches events before they reach any element
    const options = { passive: false, capture: true };
    document.addEventListener("wheel", preventPageZoom, options);

    return () => {
      document.removeEventListener("wheel", preventPageZoom, options);
    };
  }, [isOpen]);

  useEffect(() => {
    // Reset zoom and pan when modal opens
    if (isOpen) {
      resetZoom();
      resetPan();
      setImageLoaded(false);
    }
  }, [isOpen, resetZoom, resetPan]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Artwork Preview"
      description="Preview artwork with zoom functionality"
      className={cn(
        "flex flex-col",
        !isDesktop &&
          "translate-none bottom-0 left-0 top-auto h-auto max-h-[90vh] w-full rounded-b-none",
        isDesktop && "h-auto max-h-[90vh] w-fit"
      )}
    >
      <ModalHeader onClose={onClose} className="justify-between border-b-0 p-4">
        <span>Artwork Preview</span>
      </ModalHeader>

      <ModalBody className="flex items-center justify-center p-4">
        <div
          ref={containerRef}
          className={cn(
            "bg-cta-gradient relative overflow-hidden rounded-lg p-px",
            isDesktop ? "h-[540px] w-[540px]" : "w-full"
          )}
          style={{
            ...(!isDesktop &&
              containerDimensions.width !== "100%" && {
                width: containerDimensions.width,
                height: containerDimensions.height
              })
          }}
        >
          <div className="relative h-full w-full overflow-hidden rounded-md bg-white">
            <ZoomControls
              zoomPercentage={zoomPercentage}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
            />

            <div
              ref={imageContainerRef}
              className={cn(
                "relative h-full w-full",
                zoom > ZOOM_CONFIG.default ? "cursor-grab" : "",
                isPanning ? "cursor-grabbing" : ""
              )}
              {...panHandlers}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: zoom > ZOOM_CONFIG.default ? "hidden" : "visible",
                touchAction: "none"
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt={imageAlt}
                onLoad={handleImageLoad}
                className="max-w-none select-none object-contain"
                style={{
                  width: "100%",
                  height: "auto",
                  display: imageLoaded ? "block" : "none",
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                  transformOrigin: "center center"
                }}
                data-testid="artwork-preview-image"
                draggable={false}
              />
              {!imageLoaded && (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="border-t-0 p-4">
        <Button
          onPress={
            onSelect
              ? () => {
                  onSelect();
                  onClose();
                }
              : () => {}
          }
          data-testid="select-artwork-button"
          className={cn(
            "w-full",
            isSelected
              ? "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
              : "bg-cta-gradient disabled:opacity-50"
          )}
          isDisabled={isSelected || !onSelect}
        >
          {isSelected ? "Selected" : "Select Artwork"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
